import { SKIN_MANIFEST } from '../skins/manifest';
import { cardArt } from './card-manifest';
import { findItemDefinition } from './definitions';
import { GENERATED_JEWEL_MANIFEST } from './jewel-manifest.generated';
import { findPet } from './pets';

/**
 * The ONE place a definition becomes a picture.
 *
 * ## Why this is not `definitionId`
 *
 * For roughly half the catalog the two happen to coincide - `ship7` is drawn by
 * `ship7`, `artifact_guns` by `artifact_guns` - and that coincidence is exactly
 * what made the marketplace ship a `?` on its cards. It does NOT hold for:
 *
 * - **pets**, whose art belongs to the evolution rather than to the pet:
 *   `pet_apache_prismatic` is drawn by `pet_apache_ev1`, `_ev2` or `_ev3`;
 * - **cannons**, whose asset id is a property of the cannon catalog entry.
 *
 * The energy spheres and the paint buckets USED to be on that list: twenty-five
 * ids shared two pictures, and `sphere_energy_plasma_red` was drawn by
 * `pet_energy_sphere`. V28I.BL ended that — each variant now has its own art,
 * generated from the very colour and material the definition already carries —
 * and their key is their own id, like a ship's.
 *
 * Every surface therefore asks here instead of assembling a path, and a
 * category added later is wrong in one file rather than in four.
 *
 * ## The level matters
 *
 * A pet at EV. III must show the EV. III thumbnail - the same one the inventory
 * already draws. This resolves the FORM, so a listing, a trade offer and an
 * inventory cell all agree about what the player is looking at. There is no 3D
 * here and there must not be: a grid of cards is a grid of PNGs, never a grid
 * of renderers.
 *
 * ## Null is an answer
 *
 * Some definitions genuinely have no art, and `null` says so honestly: the
 * surface draws its placeholder. Inventing a path would turn a missing picture
 * into a broken request.
 *
 * The list is SHORTER since V28I.BL, which gave the two pet foods, the Quantum
 * Stabiliser, the ten Energy Spheres and the fifteen Tech Paint Buckets their
 * own art. What still answers `null` is a definition whose master nobody has
 * authored.
 */

/** What a surface shows in place of art that does not exist. Never an id. */
export const MISSING_ART_LABEL = 'Imagem indisponível';

/**
 * The manifest asset id that draws one definition at one level, or `null`.
 *
 * `level` is the pet's evolution and is ignored by everything else: no other
 * category changes its picture as it levels.
 */
export function itemAssetKey(definitionId: string, level = 1): string | null {
  const pet = findPet(definitionId);
  if (pet) {
    // A level outside the known forms falls back to MK I rather than to
    // nothing: a pet with a picture must never render as one without.
    const form = pet.forms[level - 1] ?? pet.forms[0];
    return form?.assetKey ?? null;
  }
  return findItemDefinition(definitionId)?.assetKey ?? null;
}

/**
 * **Onde a imagem de uma definição está**, seja qual for o manifesto que a tem.
 *
 * ## Quatro manifestos, e porque isto não é um quinto
 *
 * O manifesto GERAL (`manifest.json`) descreve naves, canhões, artefactos, pets,
 * equipamento, fundos e folhas de criatura, e resolve-se por `assetKey`. As
 * cartas, as joias e as skins têm manifestos PRÓPRIOS, emitidos pelos seus
 * emissores e embutidos no pacote partilhado, e resolvem-se por caminho de
 * ficheiro — cada um com o seu `sha256` e, no caso das joias, com a máscara ao
 * lado.
 *
 * Isto não escolhe nem inventa nada: pergunta aos quatro resolvedores que já
 * existem, pela ordem em que eles são donos da resposta, e devolve a de quem
 * responder. Um caminho composto à mão — `jewels/${id}.png` — produziria um
 * pedido para um ficheiro que pode não estar lá, e é exatamente o que os quatro
 * manifestos existem para impedir.
 *
 * ## A skin vem ANTES da chave geral, e é a ordem que importa
 *
 * Uma skin tem `assetKey` no catálogo — `skin-golden-wing` — e esse `assetKey`
 * **não está no manifesto geral**: ele identifica a entrada no manifesto das
 * skins, que é o dono da arte delas. Perguntar primeiro ao geral devolveria uma
 * chave que ele não descreve, e quem desenhasse ficaria sem imagem para uma skin
 * cuja arte existe. Encontrado no gate V28I.BI.1, com uma skin numa carga de
 * campo — elas caem de planetas e de pacotes de boss.
 *
 * ## `null` é uma resposta
 *
 * **Já não é a resposta para os consumíveis** (gate V28I.BL): as duas rações, o
 * Estabilizador Quântico, as dez Esferas de Energia e os quinze Baldes de Tinta
 * têm arte, cada um a sua. Continua a ser a resposta para qualquer definição cujo
 * master ninguém autorou, e quem desenha mostra o recuo que já usa — nunca o id,
 * e nunca a arte de outro item.
 */
export type ItemArt =
  /** No manifesto geral, por chave. Resolve-se com `staticAssetUrl`. */
  | { readonly kind: 'asset'; readonly assetKey: string }
  /** Num manifesto próprio, por caminho. Resolve-se com o `baseUrl` à frente. */
  | { readonly kind: 'file'; readonly file: string };

export function itemArt(definitionId: string, level = 1): ItemArt | null {
  /* A skin primeiro. Ver a nota acima: o `assetKey` dela é de outro manifesto. */
  const skin = SKIN_MANIFEST.entries.find((entry) => entry.definitionId === definitionId);
  if (skin !== undefined) {
    return { kind: 'file', file: skin.asset.file };
  }
  const assetKey = itemAssetKey(definitionId, level);
  if (assetKey !== null) {
    return { kind: 'asset', assetKey };
  }
  const card = cardArt(definitionId);
  if (card !== null) {
    return { kind: 'file', file: card.file };
  }
  const jewel = GENERATED_JEWEL_MANIFEST.entries.find(
    (entry) => entry.definitionId === definitionId,
  );
  if (jewel !== undefined) {
    return { kind: 'file', file: jewel.file };
  }
  return null;
}
